/**
 * Cross-browser clipboard copy that never throws.
 * Safari (especially iOS / older versions) is strict about the async Clipboard
 * API: it may be undefined, or reject without a fresh user gesture. We try the
 * modern API first, then fall back to a hidden-textarea execCommand copy.
 *
 * @param {string} text
 * @returns {Promise<boolean>} true if the text was copied
 */
export async function copyToClipboard(text) {
  // Modern async Clipboard API (secure contexts only)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn("Clipboard API failed, falling back:", err);
    }
  }

  // Legacy fallback: hidden textarea + execCommand (works in Safari with a gesture)
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, ta.value.length); // iOS Safari needs an explicit range
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (err) {
    console.warn("Fallback clipboard copy failed:", err);
    return false;
  }
}
